(() => {
  const root = document.querySelector('[data-gift-guide-root]');
  if (!root) return;

  const dataElement = root.querySelector('[data-gift-guide-data]');
  if (!dataElement) return;

  let data;
  try {
    data = JSON.parse(dataElement.textContent);
  } catch (error) {
    console.error('Gift Guide data could not be read.', error);
    return;
  }

  const products = Object.fromEntries((data.products || []).map((product) => [product.key, product]));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Keep merchandising configuration together so future edits only need to change these handles.
  const PERSONAS = {
    traveller: {
      title: 'Traveller',
      description: "For the one who's always going somewhere.",
      products: [
        'musafir-gift-box',
        'the-little-watercolour-kit',
        'wanderlust-mini-painting-kit',
        'solo-trip-mini-painting-kit',
        'la-dolce-vita-mini-painting-kit',
        'mini-travel-journal-a6',
        'colour-me-happy-wander',
        'vacay-mode',
      ],
    },
    painter: {
      title: 'Painter',
      description: "For the one who'd rather be making something.",
      products: [
        'art-lovers-gift-box',
        'pichwai-art-kit',
        'madhubani-art-kit',
        'dosti-gond-painting-kit',
        'la-dolce-vita-mini-painting-kit',
        'koi-pond-tote-painting-kit',
        'art-journal-a5',
        'canvas-art-pouch',
      ],
    },
    'memory-keeper': {
      title: 'Memory Keeper',
      description: 'For the one who keeps the little things.',
      products: [
        'feelin-alive-gift-box',
        'memory-album',
        'memory-wallet',
        'begin-anywhere-creative-journaling-kit',
        'pocket-journaling-kit',
        'art-journal-a5',
        'colour-me-happy-kindred',
        'art-is-cheaper-than-therapy-tote-bag',
      ],
    },
    'young-creator': {
      title: 'Young Creator',
      description: 'For curious hands and big creative energy.',
      products: [
        'feelin-alive-gift-box',
        'summer-camp-in-a-box',
        'main-character-energy-mini-painting-kit',
        'no-drama-llamas-mini-painting-kit',
        'dont-kill-my-vibe-mini-painting-kit',
        'starry-night-painting-kit',
        'beaded-bag-charm-kit',
        'colour-me-happy-bloom',
        'chai-and-chill-madhubani-painting-kit',
      ],
    },
  };

  const EDIT_PRODUCTS = [
    'art-lovers-gift-box',
    'musafir-gift-box',
    'rang-gift-box',
    'feelin-alive-gift-box',
  ];

  const BUDGET_PRODUCTS = [
    'art-is-cheaper-than-therapy-tote-bag',
    'mini-travel-journal-a6',
    'colour-me-happy-set-of-8-postcards',
    'art-journal-a5',
    'canvas-art-pouch',
    'memory-wallet',
    'beaded-bag-charm-kit',
    'memory-album',
    'wanderlust-mini-painting-kit',
    'solo-trip-mini-painting-kit',
    'la-dolce-vita-mini-painting-kit',
    'main-character-energy-mini-painting-kit',
    'dont-kill-my-vibe-mini-painting-kit',
    'chai-and-chill-madhubani-painting-kit',
    'koi-pond-tote-painting-kit',
    'the-little-watercolour-kit',
    'vacay-mode',
    'dosti-gond-painting-kit',
    'pichwai-art-kit',
    'art-lovers-gift-box',
    'begin-anywhere-creative-journaling-kit',
    'musafir-gift-box',
    'rang-gift-box',
    'summer-camp-in-a-box',
    'feelin-alive-gift-box',
  ];

  const BUDGETS = {
    'under-800': {
      description: 'Thoughtful picks under ₹800.',
      matches: (price) => price < 800,
    },
    '800-1199': {
      description: 'Giftable picks from ₹800 to ₹1,199.',
      matches: (price) => price >= 800 && price < 1200,
    },
    // ₹1,500 belongs to the final band so the controls remain non-overlapping.
    '1200-1500': {
      description: 'Creative gifts from ₹1,200 to ₹1,499.',
      matches: (price) => price >= 1200 && price < 1500,
    },
    '1500-plus': {
      description: 'Go a little bigger with gifts from ₹1,500.',
      matches: (price) => price >= 1500,
    },
  };

  const recommendationPanel = root.querySelector('[data-recommendation-panel]');
  const recommendationEmpty = root.querySelector('[data-recommendation-empty]');
  const recommendationContent = root.querySelector('[data-recommendation-content]');
  const recommendationTitle = root.querySelector('[data-recommendation-title]');
  const recommendationDescription = root.querySelector('[data-recommendation-description]');
  const recommendationGrid = root.querySelector('[data-recommendation-grid]');
  const recommendationMinimise = root.querySelector('[data-recommendation-minimise]');
  const editGrid = root.querySelector('[data-edit-grid]');
  const budgetEmpty = root.querySelector('[data-budget-empty]');
  const budgetContent = root.querySelector('[data-budget-content]');
  const budgetDescription = root.querySelector('[data-budget-description]');
  const budgetGrid = root.querySelector('[data-budget-grid]');

  const scrollTo = (element) => {
    element?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  };

  const createProductCard = (product) => {
    if (!product || !product.url) return null;

    const link = document.createElement('a');
    link.className = 'gift-guide__product-card';
    link.href = product.url;
    link.setAttribute('aria-label', `${product.title}${product.available ? '' : ' — sold out'}`);

    const media = document.createElement('span');
    media.className = 'gift-guide__product-media';
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = product.imageAlt || product.title;
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);
    }
    if (!product.available) {
      const status = document.createElement('span');
      status.className = 'gift-guide__product-status';
      status.textContent = 'Sold out';
      media.append(status);
    }
    link.append(media);

    const details = document.createElement('span');
    details.className = 'gift-guide__product-details';
    const title = document.createElement('span');
    title.className = 'gift-guide__product-title';
    title.textContent = product.title;
    details.append(title);

    const priceRow = document.createElement('span');
    priceRow.className = 'gift-guide__product-price';
    if (product.compareAtPrice && product.compareAtPrice > product.price) {
      const compareAt = document.createElement('s');
      compareAt.textContent = product.compareAtPriceDisplay || '';
      priceRow.append(compareAt, ' ');
    }
    priceRow.append(product.priceDisplay || '');
    details.append(priceRow);
    link.append(details);

    return link;
  };

  const renderCards = (container, keys) => {
    const fragment = document.createDocumentFragment();
    keys.forEach((key) => {
      const card = createProductCard(products[key]);
      if (card) fragment.append(card);
    });
    container.replaceChildren(fragment);
  };

  const updatePersonaButtons = (activeKey) => {
    root.querySelectorAll('[data-persona]').forEach((button) => {
      const isActive = button.dataset.persona === activeKey;
      button.setAttribute('aria-pressed', String(isActive));
    });
  };

  const setPersona = (key, { updateUrl = true, scroll = true } = {}) => {
    const persona = PERSONAS[key];
    if (!persona) return;

    updatePersonaButtons(key);
    recommendationTitle.textContent = persona.title;
    recommendationDescription.textContent = persona.description;
    renderCards(recommendationGrid, persona.products);
    recommendationEmpty.hidden = true;
    recommendationContent.hidden = false;
    recommendationPanel.classList.add('is-active');
    recommendationMinimise?.setAttribute('aria-label', `Minimise ${persona.title} recommendations`);
    recommendationMinimise?.setAttribute('title', `Minimise ${persona.title} recommendations`);

    if (updateUrl && window.location.hash !== `#${key}`) {
      window.history.pushState({ giftGuidePersona: key }, '', `#${key}`);
    }
    if (scroll) window.setTimeout(() => scrollTo(recommendationPanel), 30);
  };

  recommendationMinimise?.addEventListener('click', () => {
    recommendationContent.hidden = true;
    recommendationEmpty.hidden = false;
    recommendationPanel.classList.remove('is-active');
  });

  const setBudget = (key, { scroll = false } = {}) => {
    const budget = BUDGETS[key];
    if (!budget) return;

    root.querySelectorAll('[data-budget]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.budget === key));
    });
    const keys = BUDGET_PRODUCTS.filter((productKey) => products[productKey] && budget.matches(Number(products[productKey].price)));
    budgetDescription.textContent = budget.description;
    renderCards(budgetGrid, keys);
    budgetEmpty.hidden = true;
    budgetContent.hidden = false;
    root.querySelector('[data-budget-results]').classList.add('is-active');
    if (scroll) window.setTimeout(() => scrollTo(root.querySelector('[data-budget-results]')), 30);
  };

  root.querySelectorAll('[data-persona]').forEach((button) => {
    button.addEventListener('click', () => setPersona(button.dataset.persona));
  });

  root.querySelectorAll('[data-budget]').forEach((button) => {
    button.addEventListener('click', () => setBudget(button.dataset.budget));
  });

  root.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const target = document.getElementById(link.getAttribute('href').slice(1));
      if (!target) return;
      event.preventDefault();
      window.history.pushState({}, '', link.getAttribute('href'));
      scrollTo(target);
    });
  });

  const handleHash = () => {
    const key = window.location.hash.slice(1);
    if (PERSONAS[key]) setPersona(key, { updateUrl: false, scroll: true });
  };

  window.addEventListener('popstate', handleHash);
  window.addEventListener('hashchange', handleHash);

  renderCards(editGrid, EDIT_PRODUCTS);
  if (window.location.hash) handleHash();
})();
